import { ReactElement } from 'react';
import { useMutation } from '@apollo/client/react';
import { useForm, useStore } from '@tanstack/react-form';
import { CheckboxGroup, Modal, ModalFooter } from '@sc/shared-ui';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import {
  GQL_MUTATION_SAVE_PAYMENT_METHOD,
  GQL_QUERY_LIST_PAYMENT_METHODS,
} from '../utility/queries';

export interface EditablePaymentMethod {
  uuid: string;
  useForMonthlyPlan: boolean;
}

interface Props {
  open: boolean;
  card: EditablePaymentMethod | null;
  onClose: () => void;
}

const EditForm = ({
  card,
  onClose,
}: {
  card: EditablePaymentMethod;
  onClose: () => void;
}): ReactElement => {
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const [savePaymentMethod, { loading }] = useMutation(
    GQL_MUTATION_SAVE_PAYMENT_METHOD,
    {
      onError,
      refetchQueries: [GQL_QUERY_LIST_PAYMENT_METHODS],
      awaitRefetchQueries: true,
      onCompleted: () => {
        notify.success('Payment method updated.');

        onClose();
      },
    }
  );

  const form = useForm({
    defaultValues: {
      useForMonthlyPlan: card.useForMonthlyPlan,
    },
    onSubmit: async ({ value }) => {
      await savePaymentMethod({
        variables: {
          input: {
            paymentMethodId: null,
            paymentMethodUuid: card.uuid,
            useForMonthlyPlan: value.useForMonthlyPlan,
            billingDetails: null,
          },
        },
      });
    },
  });

  const values = useStore(form.store, (s) => s.values);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        void form.handleSubmit();
      }}
    >
      <div className="p-5 space-y-5">
        <p className="text-sm text-text-low-priority">
          Choose whether this card pays your monthly plan. Assigning it moves
          the plan off any other card.
        </p>
        <CheckboxGroup
          options={[
            {
              label: 'Use for monthly plan',
              value: 'useForMonthlyPlan',
              checked: values.useForMonthlyPlan,
            },
          ]}
          setChecked={({ value, checked }) =>
            form.setFieldValue(value, checked)
          }
        />
      </div>
      <ModalFooter
        asForm
        submitText="Save"
        closeText="Cancel"
        onClose={onClose}
        buttonsDisabled={loading}
      />
    </form>
  );
};

export const EditPaymentMethodModal = ({
  open,
  card,
  onClose,
}: Props): ReactElement => (
  <Modal
    open={open}
    onClose={onClose}
    title="Edit payment method"
    dialogPanelClassname="w-120"
  >
    {card ? (
      <EditForm key={card.uuid} card={card} onClose={onClose} />
    ) : (
      <div className="p-5" />
    )}
  </Modal>
);
